import { inject, Injectable } from '@angular/core';
import { reverse, sortBy } from 'lodash-es';
import { Observable, Subscriber } from 'rxjs';

import { TrialInsightsState } from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';

import { getTotalInvestigatorCostsQuery, SortOrder } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { decimalAddAll, decimalRoundingToNumber } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import {
  TICICChartLabel,
  TICICChartType,
  TrialInsightsClinicalInvestigatorCostChartService,
} from './investigator-cost-chart.service';
import { TrialInsightsClinicalInvestigatorCostTableService } from './investigator-cost-table.service';
import {
  GenericTrialInsightsQuery,
  SortOrderDefault,
} from '../../classes/trial-insights-query.class';
import {
  TrialInsightInvestigatorCostKey,
  TrialInsightsTableRowData,
} from '../../models/trial-insights-table.model';

enum Cost {
  PATIENT_PROTOCOL_DISCONTINUED = 'Discontinued',
  PATIENT_PROTOCOL_OTHER = 'Patient Invoiceables',
  PATIENT_PROTOCOL_OVERHEAD = 'Site Invoiceables',
  PATIENT_PROTOCOL_PATIENT_VISIT = 'Visit Costs',
  PATIENT_PROTOCOL_SCREEN_FAIL = 'Screen Fails',
}
type Results = State['data'];

type State = Partial<TrialInsightsState['investigatorCost']>;

const sortDefaults: SortOrderDefault[] = [
  {
    buttonKey: TrialInsightInvestigatorCostKey.INVESTIGATOR,
    defaultOrder: SortOrder.DESC,
  },
];

@Injectable()
export class TrialInsightsClinicalInvestigatorCostQueryService extends GenericTrialInsightsQuery<TICICChartType> {
  chartService: TrialInsightsClinicalInvestigatorCostChartService;
  tableService: TrialInsightsClinicalInvestigatorCostTableService;
  trialInsightsQuery: TrialInsightsQuery;

  constructor() {
    const trialInsightsQuery = inject(TrialInsightsQuery);
    const tableService = inject(TrialInsightsClinicalInvestigatorCostTableService);
    const chartService = inject(TrialInsightsClinicalInvestigatorCostChartService);

    super({
      chartService,
      slice: 'investigatorCost',
      sortDefaults,
      tableService,
      trialInsightsQuery,
    });

    this.trialInsightsQuery = trialInsightsQuery;
    this.tableService = tableService;
    this.chartService = chartService;
  }

  processResponseData() {
    const processFn = (sourceValue: Results, subscriber: Subscriber<Results>) => {
      if (!sourceValue?.length) {
        this.resetState();

        return;
      }

      const data = reverse(sortBy(sourceValue, 'cost'));

      const costs = data.map((cost) => cost.cost);

      const total = decimalAddAll(5, ...costs);

      const labels: TICICChartLabel = data.map((cost) => {
        const label = Cost[cost.patient_protocol_type];

        const percentTotal = Utils.calculateAsPercentage(total, cost.cost);
        const percent = decimalRoundingToNumber(percentTotal, 2);

        return [label, percent];
      });

      const rowData: TrialInsightsTableRowData[] = data.map((cost) => {
        return {
          buttonKey: TrialInsightInvestigatorCostKey.INVESTIGATOR,
          leftHeader: Cost[cost.patient_protocol_type],
          leftSubheader: '',
          rightHeader: Utils.currencyFormatter(cost.cost),
          rightSubheader: '',
        };
      });

      const datasets = this.chartService.createDatasets(labels);
      const chartOptions = this.chartService.createChart(datasets);
      const tableOptions = this.tableService.createTable(rowData);
      const totalRound = decimalRoundingToNumber(total, 2);

      this.chartOptions.next(chartOptions);
      this.tableOptions.next(tableOptions);
      this.totalAmount.next(Utils.currencyFormatter(totalRound));

      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  processTotalInvestigatorCosts$(): Observable<Results> {
    const data = this.data as Observable<getTotalInvestigatorCostsQuery[]>;

    return data.pipe(this.processResponseData());
  }
}
